// Quelle: /opt/webgis/frontend/src/screens/dashboard/shared.tsx — `Menu` (Z. 154–265),
// `chipStyle` (Z. 267–291), `SegSwitch` (Z. 488–505) (Stand 2026-09-29).
// Geändert (SPEC 03 §8.4): als `Menue`, `chipStil`, `SegSchalter` ausgeführt; Texte deutsch;
// Radius var(--r-menue); role="menu" auf der Liste, ↑/↓/Pos1/Ende wandern durch die Einträge,
// der Fokus springt beim Öffnen auf den ersten Eintrag und beim Schließen zum Auslöser zurück.
// Die beiden Riegel bleiben wörtlich: ein Klick in ein GESTAPELTES Popup (Auswahlliste, Modal
// aus dem Menü) schließt das Menü nicht (`inStackedPopup`), und solange ein Modal offen ist,
// gehört ihm die Escape-Taste (`modalOffen`). Das Menü schluckt sein Escape (stopPropagation),
// damit der Esc-Stapel der Shell nicht zusätzlich die Ebene darunter schließt.
// Neu dazu: `MenueKnopf` (Auslöser mit aria-haspopup/aria-expanded + Menü) und `MenuePunkt`.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { Icon } from "./Icon";
import { Btn, IconBtn, type BtnProps } from "./primitives";

/**
 * ausserhalb. Ohne diese Ausnahme schloss der mousedown das Panel, die Liste
 * verschwand mit ihm und der Klick auf die Option kam nie an: genau der Grund,
 * warum sich im Filter kein Feld auswaehlen liess.
 */
function inStackedPopup(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null;
  // Beides liegt im Portal UEBER dem Panel und darf es nicht schliessen: die
  // Auswahlliste eines Select — und ein Dialog, den das Panel selbst geoeffnet
  // hat (etwa die Rueckfrage vor dem Loeschen). Ohne den zweiten Fall stand man
  // nach einem Abbrechen vor einem zugeklappten Menue.
  return !!el?.closest('[role="listbox"], .modal-wrap');
}

/** Steht gerade irgendein Dialog offen? Dann gehoert ihm die Escape-Taste. */
function modalOffen(): boolean {
  return !!document.querySelector(".modal-wrap");
}

const EINTRAG = '[role="menuitem"]:not([disabled]), [role="menuitemcheckbox"]:not([disabled]), [role="menuitemradio"]:not([disabled])';

/**
 * Kleines Dropdown-Panel — schliesst beim Klick nach aussen. Filterleiste,
 * Kacheln und Prüfen teilen sich dieselbe Mechanik.
 */
export function Menue({
  open,
  onClose,
  width = 260,
  align = "right",
  children,
  label,
  id,
  style,
}: {
  open: boolean;
  onClose: () => void;
  width?: number;
  /** an welcher Kante des Ausloesers das Panel haengt (Filterchips: links) */
  align?: "left" | "right";
  children: ReactNode;
  /** Name der Liste für Hilfstechnik */
  label?: string;
  id?: string;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const vorherRef = useRef<Element | null>(null);
  /**
   * Randkorrektur. Das Panel haengt an einer Kante des Ausloesers — bei
   * align="right" oeffnet es nach LINKS. Steht der Ausloeser auf einem
   * schmalen Schirm weit links, landet die linke Kante im Negativen und die
   * halbe Liste steht ausserhalb des Bildes. `maxWidth` allein hilft nicht:
   * das begrenzt die Breite, nicht die Position. Also nach dem Oeffnen messen
   * und nur so weit schieben, wie noetig ist — auf dem Desktop ist die
   * Verschiebung immer 0.
   */
  const [schub, setSchub] = useState(0);
  // Der zuletzt gesetzte Schub als Ref: der Effekt haengt NICHT an `schub`. Sonst loest jede
  // Korrektur einen neuen Messlauf aus — und wo das Messen den Schub nicht sieht (happy-dom
  // kennt kein Layout, CI 30.09.: "Maximum update depth exceeded"), waechst er endlos.
  const schubRef = useRef(0);
  useLayoutEffect(() => {
    if (!open) {
      schubRef.current = 0;
      setSchub(0);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const messen = () => {
      const r = el.getBoundingClientRect();
      // Ohne den bereits gesetzten Schub rechnen, sonst schaukelt sich die
      // Korrektur ueber mehrere Durchlaeufe auf.
      const links = r.left - schubRef.current;
      const rechts = r.right - schubRef.current;
      const rand = 8;
      let d = 0;
      if (links < rand) d = rand - links;
      else if (rechts > window.innerWidth - rand) d = window.innerWidth - rand - rechts;
      // Ein Panel, das breiter als der Schirm ist, wird an den linken Rand
      // gelegt statt hin- und hergeschoben.
      if (rechts - links > window.innerWidth - 2 * rand) d = rand - links;
      if (Math.abs(d - schubRef.current) > 0.5) {
        schubRef.current = d;
        setSchub(d);
      }
    };
    messen();
    window.addEventListener("resize", messen);
    return () => window.removeEventListener("resize", messen);
  }, [open]);

  // Fokus hinein beim Öffnen, zurück zum Auslöser beim Schließen.
  useEffect(() => {
    if (!open) return;
    vorherRef.current = document.activeElement;
    const erster = ref.current?.querySelector<HTMLElement>(EINTRAG);
    erster?.focus({ preventScroll: true });
    return () => {
      const z = vorherRef.current;
      // Nur zurück, wenn der Fokus noch im (jetzt verschwundenen) Menü war oder nirgends ist —
      // ein Klick auf ein anderes Feld soll dort bleiben dürfen.
      const aktiv = document.activeElement;
      if (z instanceof HTMLElement && (!aktiv || aktiv === document.body || !document.contains(aktiv))) z.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (inStackedPopup(e.target)) return;
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (modalOffen()) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const wandern = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const liste = Array.from(ref.current?.querySelectorAll<HTMLElement>(EINTRAG) ?? []);
    if (!liste.length) return;
    e.preventDefault();
    const i = liste.indexOf(document.activeElement as HTMLElement);
    let j = 0;
    if (e.key === "Home") j = 0;
    else if (e.key === "End") j = liste.length - 1;
    else if (e.key === "ArrowDown") j = i < 0 ? 0 : (i + 1) % liste.length;
    else j = i < 0 ? liste.length - 1 : (i - 1 + liste.length) % liste.length;
    liste[j]?.focus();
  };

  if (!open) return null;
  return (
    <div
      ref={ref}
      id={id}
      role="menu"
      aria-label={label}
      className="anim-pop menue"
      onKeyDown={wandern}
      style={{
        position: "absolute",
        left: align === "left" ? 0 : undefined,
        right: align === "right" ? 0 : undefined,
        top: "calc(100% + 6px)",
        width,
        maxWidth: "calc(100vw - 16px)",
        transform: schub ? `translateX(${schub}px)` : undefined,
        borderRadius: "var(--r-menue)",
        padding: 6,
        zIndex: 60,
        background: "var(--panel-solid)",
        border: "1px solid var(--glass-border-strong)",
        boxShadow: "var(--shadow-lg)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Ein Eintrag im Menü. `an` (true/false) macht ihn zum menuitemcheckbox mit Haken. */
export function MenuePunkt({
  icon,
  children,
  onClick,
  an,
  gefahr,
  disabled,
  sub,
  title,
}: {
  icon?: string;
  children: ReactNode;
  onClick: () => void;
  an?: boolean;
  gefahr?: boolean;
  disabled?: boolean;
  /** kleine Zusatzangabe rechts (mono), z. B. ein Tastenkürzel */
  sub?: string;
  title?: string;
}) {
  const checkbox = an !== undefined;
  return (
    <button
      type="button"
      role={checkbox ? "menuitemcheckbox" : "menuitem"}
      aria-checked={checkbox ? an : undefined}
      disabled={disabled}
      title={title}
      className={`menue-punkt ring-focus${gefahr ? " gefahr" : ""}`}
      onClick={onClick}
    >
      {checkbox ? <Icon name={an ? "check" : "minus"} size={15} style={{ opacity: an ? 1 : 0 }} /> : icon ? <Icon name={icon} size={16} /> : null}
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {sub && <span className="sub">{sub}</span>}
    </button>
  );
}

/** Kleine Zwischenüberschrift im Menü. */
export function MenueTitel({ children }: { children: ReactNode }) {
  return (
    <div className="menue-titel" role="presentation">
      {children}
    </div>
  );
}

/**
 * Auslöser + Menü in einem: der Knopf trägt aria-haspopup="menu" und aria-expanded, das
 * Menü hängt darunter. `kinder` bekommt eine Schließfunktion, damit ein Eintrag das Menü
 * nach seiner Handlung zumachen kann.
 */
export function MenueKnopf({
  icon,
  label,
  title,
  variant = "ghost",
  size = "sm",
  nurSymbol = false,
  width,
  align = "right",
  disabled,
  kinder,
  knopfStil,
  className,
}: {
  icon?: string;
  label?: ReactNode;
  title: string;
  variant?: BtnProps["variant"];
  size?: BtnProps["size"];
  /** IconBtn statt Btn (Kopfzeilen, Kacheln) */
  nurSymbol?: boolean;
  width?: number;
  align?: "left" | "right";
  disabled?: boolean;
  kinder: (schliessen: () => void) => ReactNode;
  knopfStil?: CSSProperties;
  className?: string;
}) {
  const [offen, setOffen] = useState(false);
  const menueId = useId();
  const schliessen = useCallback(() => setOffen(false), []);
  const gemeinsam = {
    "aria-haspopup": "menu" as const,
    "aria-expanded": offen,
    "aria-controls": offen ? menueId : undefined,
    disabled,
    onClick: () => setOffen((o) => !o),
  };
  return (
    <div className={`menue-anker ${className ?? ""}`} style={{ position: "relative", display: "inline-flex" }}>
      {nurSymbol ? (
        <IconBtn type="button" icon={icon ?? "dots-three"} title={title} active={offen} style={knopfStil} {...gemeinsam} />
      ) : (
        <Btn type="button" variant={variant} size={size} icon={icon} title={title} style={knopfStil} {...gemeinsam}>
          {label}
          <Icon name="caret-down" size={13} style={{ opacity: 0.7 }} />
        </Btn>
      )}
      <Menue open={offen} onClose={schliessen} width={width} align={align} id={menueId} label={title}>
        {kinder(schliessen)}
      </Menue>
    </div>
  );
}

/** Optik des Filterchips — gesetzter Filter faerbt ihn im Akzent. */
export function chipStil(on: boolean, dashed?: boolean): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12.5,
    fontWeight: 600,
    padding: "8px 13px",
    borderRadius: 11,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "none",
    border: dashed ? "1px dashed" : "1px solid",
    borderColor: on ? "var(--accent)" : "var(--field-border)",
    background: on ? "var(--accent-soft)" : "var(--field)",
    color: on ? "var(--accent-text)" : "var(--text-dim)",
    fontFamily: "inherit",
    transition: "all .15s ease",
  };
}

/** Segment-Umschalter — EINE Komponente fuer alle Umschalter, damit die Zwillinge nie
 *  auseinanderdriften. */
export function SegSchalter({
  value,
  onChange,
  optionen,
  title,
  klein,
}: {
  value: string;
  onChange: (v: string) => void;
  optionen: Array<{ id: string; label: ReactNode; icon?: string; disabled?: boolean; title?: string }>;
  title?: string;
  klein?: boolean;
}) {
  return (
    <div
      className="seg"
      role="tablist"
      aria-label={title}
      title={title}
      style={{ display: "inline-flex", alignItems: "stretch", border: "1px solid var(--glass-border-strong)", borderRadius: 10, overflow: "hidden", height: klein ? 30 : 36, padding: 0, gap: 0, maxWidth: "100%" }}
    >
      {optionen.map((o) => (
        <button
          key={o.id}
          type="button"
          role="tab"
          aria-selected={value === o.id}
          disabled={o.disabled}
          title={o.title}
          onClick={() => onChange(o.id)}
          className="ring-focus"
          style={{
            border: "none",
            cursor: o.disabled ? "not-allowed" : "pointer",
            padding: klein ? "0 10px" : "0 14px",
            fontSize: klein ? 12 : 12.5,
            fontWeight: 600,
            fontFamily: "inherit",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            whiteSpace: "nowrap",
            opacity: o.disabled ? 0.45 : 1,
            background: value === o.id ? "var(--accent-soft)" : "transparent",
            color: value === o.id ? "var(--accent-text)" : "var(--text-mute)",
          }}
        >
          {o.icon && <Icon name={o.icon} size={15} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}
